import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Badge } from '@/components/ui/Badge';
import type { InlineSaveResult } from '@/components/ui/InlineField';
import { MultiCombobox, type MultiComboboxOption } from '@/components/ui/MultiCombobox';
import { cx } from '@/lib/cx';

/**
 * What a set-valued write reported.
 *
 * `InlineSaveResult` plus the one thing a single-valued field never needs:
 * WHICH members the server refused. `regionIds` is validated as a whole set and
 * a 422 names **every** offender at once ("Not an active region: 998, 999"),
 * deliberately, so the form can mark all the bad chips in one pass instead of
 * making someone resubmit to discover the next one. Collapsing that into one
 * sentence under the control throws the useful half away.
 */
export type InlineSetSaveResult = InlineSaveResult & { invalidValues?: readonly string[] };

interface Notice {
  tone: 'success' | 'warning' | 'error' | 'partial';
  text: string;
}

/** How long a "Saved" acknowledgement stays on screen — the same as `InlineField`. */
const SAVED_NOTICE_MS = 4000;

/** The acknowledgement, worded exactly as `InlineField` words it. */
const SAVED_LEAD = 'Saved.';

/**
 * The status line's colour, per tone — the same table `InlineField` keeps, and
 * exhaustive for the same reason: a tone nobody named used to fall through to
 * `text-success`, painting an unhandled outcome as a clean save.
 */
function noticeToneClass(tone: Notice['tone']): string {
  switch (tone) {
    case 'error':
      return 'text-danger';
    case 'warning':
    case 'partial':
      return 'text-warning';
    case 'success':
      return 'text-success';
    default: {
      const unhandled: never = tone;
      throw new Error(`Unhandled notice tone: ${String(unhandled)}`);
    }
  }
}

/** Membership, not order: the server owns the order it reads a set back in. */
function sameSet(a: readonly string[], b: readonly string[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  if (left.size !== right.size) return false;
  for (const value of right) {
    if (!left.has(value)) return false;
  }
  return true;
}

/**
 * Is this node inside a popover that a picker PORTALLED to `<body>`?
 *
 * ## ⚠️ Why this exists — the bug it replaces
 *
 * The first version of this field committed from an `onBlur` on the editor
 * container, exactly as `InlineField` does. That does not work for this
 * control, and the field silently lost every edit:
 *
 * `MultiCombobox` renders its popover with `createPortal(..., document.body)`,
 * so while the list is open the focused element — its search box — is **not in
 * this component's DOM subtree at all**. Worse, `MultiCombobox` also closes
 * itself from a document-level `pointerdown` when you click away, which
 * **unmounts the focused input**, and removing a focused element fires no
 * `blur` / `focusout`. So the blur the old code waited for could never arrive:
 * no PATCH was sent, the field stayed open, and the selection was gone on
 * reload.
 *
 * The fix is to detect the leave at the document instead, which means being
 * able to ask "is this target part of my control?" for a node that has no DOM
 * ancestry in common with it.
 *
 * ## How a portalled popover is recognised
 *
 * `MultiCombobox` exposes no `onOpenChange`, no `onClose` and no ref, and
 * `src/components/ui` is not this task's to change — so the popover is found
 * from the one stable thing it renders: its search box is the element carrying
 * `role="combobox"`. Walking up from there to the child of `<body>` gives the
 * portal root without depending on a class name, a style or a z-index.
 *
 * The two rejections matter. A `role="combobox"` that is **not** portalled
 * walks up to the app root instead, which contains the whole page — treating
 * that as "inside" would mean never committing at all. So a root that contains
 * the editor is not a portal, and `<body>` itself is never an answer.
 */
function inPortalledPopover(node: Node, editor: HTMLElement | null): boolean {
  for (const combobox of document.querySelectorAll('[role="combobox"]')) {
    let root: Element = combobox;
    while (root.parentElement && root.parentElement !== document.body) {
      root = root.parentElement;
    }
    if (root === document.body) continue;
    // The app root, not a portal: it contains this very field.
    if (editor && root.contains(editor)) continue;
    if (root.contains(node)) return true;
  }
  return false;
}

export interface InlineRegionsFieldProps {
  label: string;
  /** The stored ids as strings, in the order the record reads them back. */
  values: readonly string[];
  options: MultiComboboxOption[];
  /**
   * Writes the WHOLE set and reports the outcome. Not called when the field has
   * nothing to write — `regionIds` omitted leaves the stored set alone, and an
   * empty PATCH is a 422, so an untouched field must send nothing.
   *
   * ⚠️ **"Nothing to write" is the SELECTION being unchanged AND `alsoDirty`
   * being false.** It is not "the selection is unchanged" on its own: a caller
   * holding state of its own in `editorHeader` says so through `alsoDirty`, and
   * is then called with a set identical to `values`. See that prop.
   */
  onSave: (next: string[]) => Promise<InlineSetSaveResult>;
  optionsLoading?: boolean;
  disabled?: boolean;
  /** How an empty set reads when idle. `[]` is a real state, not a gap. */
  emptyLabel?: string;
  /**
   * The IDLE read view, when a full chip list is the wrong shape for it.
   *
   * Defaults to every member as a chip, which is right for a company's regions:
   * the set holds two to four and all of them are the answer. It is wrong for a
   * set that can hold two hundred — a project's cities cap at 200 — where an
   * uncollapsed list turns one table row into a paragraph and stops the list
   * being scannable, which is the whole job of a list.
   *
   * ⚠️ **This replaces the read view only.** The EDITOR is always the full
   * multi-select and the save is always the whole set, so a collapsed display
   * can never become a collapsed write. It must contain no interactive
   * elements: it renders inside the trigger button.
   */
  display?: ReactNode;
  /**
   * Caller-supplied chrome, rendered inside the EDITOR above the picker.
   *
   * Deliberately neutral and deliberately a `ReactNode`: this component knows
   * nothing about what a caller puts here and must keep knowing nothing. It is
   * a SLOT, not a feature — naming it after its first occupant would make the
   * next caller's use of it read as a misuse.
   *
   * ## 🔴 IT RENDERS INSIDE `editorRef`, AND THAT IS THE WHOLE SAFETY PROPERTY
   *
   * "The user left the field" is detected from two DOCUMENT events and tested
   * against `editorRef.current.contains(target)` — see the effect below. A
   * control rendered OUTSIDE that container would therefore be read as a leave:
   * clicking it would COMMIT the draft and close the editor, so a view control
   * meant to change what the list offers would instead save the record. A
   * `200`, an editor that vanished, and nothing that errored.
   *
   * So the slot is inside the editor container, and anything placed in it is
   * already covered by the existing test with no change to that logic at all.
   *
   * ## ⚠️ ABOVE the picker, not below it, and not inside the popover
   *
   * **Not inside the popover, because there is no seam to put it through.** The
   * popover belongs to `MultiCombobox`, which `createPortal`s it to `<body>`
   * from inside itself and exposes no slot for arbitrary chrome — `addNew` is a
   * listbox ROW and `footnote` is a string under the list. Reaching into it
   * would mean changing a shared `ui/` primitive.
   *
   * **Above rather than below, because the popover is `position: fixed` and
   * opens at the trigger's bottom edge.** Anything rendered under the picker is
   * covered by up to 288px of open list — visible only once the list is shut,
   * which is a control the reader cannot see at the moment they want it.
   *
   * 🔴 **THAT LAST CLAUSE IS A DEFAULT, NOT A PROPERTY — corrected 2026-09-14.**
   * `MultiCombobox`'s `place()` puts the panel ABOVE the trigger when there is
   * no room below, which is straight over this slot. Measured in a browser
   * against production: at 1440 in the projects table it covered 94% of the
   * occupant, and at 768 every control in it was unreachable. **Above is still
   * the right side** — below is covered in the ordinary case, above only when
   * the trigger sits low — but the slot is not unconditionally safe, and the
   * opening gesture no longer opens the list while it is occupied. The
   * measurements and the two refused alternatives are at that effect.
   *
   * ⚠️ **KNOWN AND NOT WORKED AROUND: clicking a control in here while the list
   * is OPEN closes the list.** `MultiCombobox` closes itself from a
   * document-level `pointerdown` on anything outside its own wrapper and
   * popover, and this slot is outside both. The cost is one extra click to
   * reopen. It could be suppressed with a `stopPropagation` on the caller's
   * control — and deliberately is not: that would depend both on React's
   * root-level event attachment and on a private listener inside a component
   * this one does not own, to buy a click. A visible extra click beats a
   * silently broken close.
   */
  editorHeader?: ReactNode;
  /**
   * The caller has something to write that this component cannot see.
   *
   * Deliberately neutral and deliberately not named for its first occupant, for
   * the reason `editorHeader` gives about itself: a flag called
   * `ticksChanged` would make the next caller's use of it read as a misuse. It
   * says only *there is a second kind of dirt here*, and stays ignorant of what.
   *
   * ## Why it exists
   *
   * `commit` refuses to write an unchanged set, because `regionIds` omitted
   * leaves the stored set alone and an empty PATCH is a 422 — so for a field
   * whose whole content is the set, an unchanged set means nothing to send.
   * That stops being the whole question the moment a caller puts state of its
   * own in `editorHeader`: the membership can be untouched while the body the
   * caller would build is not empty at all. Without this, that caller's edit is
   * dropped by `revert()`, which reports nothing — a control that answers as
   * though it worked.
   *
   * It is `false` by default, so a caller that does not pass it behaves exactly
   * as before.
   *
   * ## ⚠️ It widens the DIRTY TEST and nothing else
   *
   * `revert`, `beginEditing`, the leave detection and the draft are all
   * untouched, and **`onSave` still receives the MEMBERSHIP** — here, a set
   * identical to `values`. This component has no idea what else is being
   * written and must keep having none; combining the two halves into one body
   * is the caller's job, because the caller is the only one that can.
   *
   * ## ⛔ Compute it as a DIFF against what is stored, never as "touched"
   *
   * A reader who ticks a box and unticks it has moved nothing, so this must be
   * `false` again and the field must revert with no request. **That is not
   * tidiness: with the set unchanged too, the body the caller builds is empty,
   * and an empty PATCH is a 422** — a refusal on a visit in which the reader
   * changed their mind back, which is not a refusal anybody can act on. A flag
   * raised by the first toggle and never lowered produces exactly that.
   */
  alsoDirty?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  footnote?: string;
  /** A standing caveat about the stored value — rendered in both phases. */
  note?: ReactNode;
}

/**
 * A SET, edited where it is read.
 *
 * `InlineField` is the single-valued sibling and this follows its contract
 * exactly — idle to editing to saving, Escape reverts, an untouched field fires
 * no request, a failed save keeps the selection with the message on the field.
 * It is a separate component only because the editor is a multi-select and the
 * value is an array, which `InlineField`'s three kinds cannot express.
 *
 * The one behaviour worth stating: **the save is the whole set, once, when you
 * leave the field** — not one write per chip. `regionIds` replaces everything
 * it is sent, so adding a region and removing a retired one in the same visit
 * is a single PATCH that either applies or does not. Saving per toggle would
 * make that two writes, the first of which can be refused for a chip the user
 * was on their way to deleting.
 */
export function InlineRegionsField({
  label,
  values,
  options,
  onSave,
  optionsLoading = false,
  disabled = false,
  emptyLabel = 'None',
  display,
  editorHeader,
  alsoDirty = false,
  placeholder,
  searchPlaceholder,
  footnote,
  note,
}: InlineRegionsFieldProps) {
  const uid = useId();
  const controlId = `${uid}-control`;
  const statusId = `${uid}-status`;

  /*
   * Is the slot occupied? — asked once, and read by BOTH the render gate below
   * and the opening gesture, so the two cannot disagree about whether there is
   * anything above the picker. `Boolean(...)` rather than `!== undefined`
   * deliberately: a caller passing a falsy node renders nothing, so a gate on
   * definedness would reserve room for a header that is not on screen.
   */
  const hasEditorHeader = Boolean(editorHeader);

  const [phase, setPhase] = useState<'idle' | 'editing' | 'saving'>('idle');
  const [draft, setDraft] = useState<string[]>([...values]);
  const [notice, setNotice] = useState<Notice | null>(null);
  /** The ids the server named in its last refusal, so each bad chip is marked. */
  const [refused, setRefused] = useState<readonly string[]>([]);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  /**
   * The PICKER's own wrapper, which `editorRef` is not: the editor may also
   * hold `editorHeader`, and the opening gesture below has to find the picker
   * rather than whatever the caller put above it.
   */
  const controlRef = useRef<HTMLDivElement>(null);
  const wantsEditorFocus = useRef(false);
  const wantsTriggerFocus = useRef(false);
  /** Two quick commits can resolve out of order; only the newest may land. */
  const saveSeq = useRef(0);
  /*
   * A leave is detected from TWO document events (see the effect below), and a
   * click that moves focus fires both. This latches synchronously — before
   * either handler can await anything — so one leave is one save. `phase` alone
   * cannot do it: both handlers run against the same render's stale value.
   */
  const leaving = useRef(false);

  useEffect(() => {
    if (phase !== 'editing' || !wantsEditorFocus.current) return;
    wantsEditorFocus.current = false;
    /*
     * ⚠️ **`controlRef`, not `editorRef` — and this is not tidying.** It used to
     * read the first `<button>` in the whole editor, which was the picker's
     * trigger only for as long as the picker was the only thing in there.
     * `editorHeader` renders ABOVE it and is expected to hold buttons, so the
     * old query would focus and then CLICK a caller's control instead: the list
     * would not open, the one-gesture edit would become two, and the caller's
     * control would be pressed by nobody. Nothing would error.
     */
    const control = controlRef.current?.querySelector<HTMLElement>('button');
    if (!control) return;
    /*
     * ⚠️ **Focusing is UNCONDITIONAL and is load-bearing beyond convenience.**
     * The idle trigger has just unmounted, so with nothing focused here focus
     * falls to `<body>` — which the document `focusin` below reads as a leave,
     * committing and closing the editor in the frame it opened. Whatever else
     * changes about the opening gesture, something inside `editorRef` must end
     * up focused.
     */
    control.focus();

    /*
     * 🔴 **THE LIST OPENS ITSELF ONLY WHEN THE SLOT IS EMPTY — 2026-09-14.**
     *
     * Opening here makes editing one interaction rather than two — the same
     * gesture the user already made, as in `InlineField` — and that is still
     * what every caller with nothing above the picker gets.
     *
     * ⛔ **It is wrong whenever `editorHeader` is occupied, because the popover
     * FLIPS.** `editorHeader`'s own doc argues the slot sits ABOVE the picker on
     * the grounds that a `position: fixed` popover opens at the trigger's BOTTOM
     * edge — true in the ordinary case, and not a property of the control:
     * `MultiCombobox`'s `place()` puts the panel ABOVE the trigger when there is
     * no room below, straight over the one region that placement was chosen to
     * keep clear.
     *
     * **Measured in a browser against production, 2026-09-14**, on `/projects`'
     * Products cell — the only occupant of this slot today:
     *
     *   1440, table view, a row near the top of an 813px viewport. The fieldset
     *     ran `top 150 → bottom 498` and the trigger `503 → 585`, leaving 228px
     *     below against a 327px panel — so it was placed `171 → 498` and covered
     *     **94% of the group**. `elementFromPoint` over all three checkboxes,
     *     and over the legend's later lines, returned nodes inside the popover.
     *   768, card view. Fieldset `523 → 635`, popover `308 → 635`: all three
     *     boxes unreachable.
     *   603px of room below. Opens downward, nothing covered, everything
     *     reachable — **so the arrangement is right and the flip defeats it.**
     *
     * Two things made it the COMMON case rather than an edge, and the auto-open
     * was one of them: the cover appeared at the very moment the ticks did. The
     * other is that a ~133px table cell forces the boxes into two ~62px columns,
     * making the group 348px tall and pushing the trigger so far down the panel
     * can essentially never fit beneath it.
     *
     * ⚠️ **Nothing errored and one Escape recovered it**, which is why a control
     * that was unreachable on arrival read as a list that had opened eagerly.
     *
     * ⛔ **Two fixes were put to the owner and REFUSED — do not re-propose
     * either.** Teaching `place()` to reserve the slot is a change to shared
     * placement logic whose idempotency this repo has already been bitten by: a
     * first implementation clamped and left a second effect to correct the
     * width, and the next `place()` overwrote the correction, leaving options
     * silently unreachable at 320. Moving the ticks BELOW the picker is refused
     * at `editorHeader` itself — the popover opens at the trigger's bottom edge
     * in the ORDINARY case, so that trades a rare cover for a constant one.
     *
     * ⚠️ **Gated on the SLOT, never on the caller.** This component knows
     * nothing about what a caller puts in `editorHeader` and must keep knowing
     * nothing — a test for the projects cell would make the next occupant of the
     * slot inherit a defect nobody had measured for it. A caller that passes the
     * slot CONDITIONALLY therefore opens its list on the visits where it passes
     * nothing, which is correct: there is nothing there to cover.
     */
    if (hasEditorHeader) return;
    control.click();
  }, [phase, hasEditorHeader]);

  useEffect(() => {
    if (phase !== 'idle' || !wantsTriggerFocus.current) return;
    wantsTriggerFocus.current = false;
    triggerRef.current?.focus();
  }, [phase]);

  useEffect(() => {
    if (notice?.tone !== 'success') return;
    const timer = setTimeout(() => setNotice(null), SAVED_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  function beginEditing() {
    if (disabled || phase !== 'idle') return;
    setDraft([...values]);
    setNotice(null);
    setRefused([]);
    wantsEditorFocus.current = true;
    setPhase('editing');
  }

  function revert(returnFocus: boolean) {
    setDraft([...values]);
    setNotice(null);
    setRefused([]);
    wantsTriggerFocus.current = returnFocus;
    setPhase('idle');
  }

  async function commit(next: string[], returnFocus: boolean) {
    if (phase !== 'editing') return;

    /*
     * Nothing to write is a state reset, never a request: an empty PATCH is a
     * 422, and re-sending a set that already holds a since-retired id is
     * another one.
     *
     * ⚠️ **`alsoDirty` is what stops "the set did not move" being read as "the
     * FIELD did not move".** A caller with state in `editorHeader` has a second
     * kind of dirt this component cannot see, and without the OR its edit is
     * reverted here with nothing reported. It is a widening of this one test
     * and reaches nothing else — `onSave` below still gets the membership.
     */
    if (!alsoDirty && sameSet(next, values)) {
      revert(returnFocus);
      return;
    }

    const ticket = saveSeq.current + 1;
    saveSeq.current = ticket;
    setNotice(null);
    setRefused([]);
    setPhase('saving');

    let result: InlineSetSaveResult;
    try {
      result = await onSave(next);
    } catch (caught) {
      result = {
        status: 'error',
        message: caught instanceof Error ? caught.message : 'The change could not be saved.',
      };
    }
    if (saveSeq.current !== ticket) return;

    /*
     * ⚠️ Exhaustive, with a `never` default — this is the SECOND renderer of
     * `InlineSaveResult`, and the pair going out of step is silent: the chain
     * this replaces read an outcome it did not name as a clean save, so a
     * member added for `InlineField` alone would have been rendered here as
     * "Saved." over whatever it was reporting. `tsc` now names this file.
     */
    switch (result.status) {
      case 'error':
        // The selection stays exactly as assembled, with every refused chip marked.
        setRefused(result.invalidValues ?? []);
        setNotice({ tone: 'error', text: result.message });
        setPhase('editing');
        return;
      case 'warning':
        settle({ tone: 'warning', text: result.message }, returnFocus);
        return;
      case 'partial':
        settle({ tone: 'partial', text: `${SAVED_LEAD} ${result.message}` }, returnFocus);
        return;
      case 'saved':
        settle({ tone: 'success', text: result.message ?? SAVED_LEAD }, returnFocus);
        return;
      default: {
        const unhandled: never = result;
        throw new Error(`Unhandled inline save outcome: ${JSON.stringify(unhandled)}`);
      }
    }
  }

  /** The outcomes that END the edit: close the editor, keep the line. */
  function settle(reported: Notice, returnFocus: boolean) {
    wantsTriggerFocus.current = returnFocus;
    setNotice(reported);
    setPhase('idle');
  }

  /*
   * The newest leave handler, so the document listeners below never commit a
   * stale draft. Refreshed on every render, in an effect rather than during
   * render because writing a ref during render is not a render output.
   */
  const leaveRef = useRef<() => void>(() => {});
  useEffect(() => {
    leaveRef.current = () => {
      // Latched synchronously: `pointerdown` and `focusin` both land on one
      // click, and only the first of them may start a write.
      if (leaving.current) return;
      leaving.current = true;
      void commit(draft, false);
    };
  });

  /**
   * "The user left the field" — detected at the document, not from a blur.
   *
   * ⚠️ **A blur cannot do this job here, and relying on one is the bug this
   * replaces.** `MultiCombobox` portals its popover to `<body>`, so while the
   * list is open the focused element is outside this component's subtree
   * entirely; and clicking away unmounts that focused input (the picker closes
   * itself from its own document `pointerdown`), which fires no `blur` and no
   * `focusout` at all. The commit simply never ran.
   *
   * So the leave is read from the two events that DO fire when focus or a
   * pointer lands somewhere else, and "somewhere else" is tested against both
   * halves of the control: this container, and whatever the picker portalled
   * out of it.
   *
   * `focusin` covers tabbing and programmatic focus; `pointerdown` covers a
   * click on something that takes no focus at all. Both are needed, both can
   * fire for one gesture, and `leaving` is what keeps that one save.
   *
   * Attached only while editing, so an idle field listens to nothing, and a
   * save in flight cannot be interrupted by a second one.
   */
  useEffect(() => {
    if (phase !== 'editing') {
      leaving.current = false;
      return;
    }

    function onLeave(event: Event) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (editorRef.current?.contains(target)) return;
      if (inPortalledPopover(target, editorRef.current)) return;
      leaveRef.current();
    }

    /*
     * CAPTURE phase, deliberately: `MultiCombobox` closes its popover from a
     * BUBBLE-phase document `pointerdown`, and once it has, the popover is
     * unmounted and a click that landed inside it can no longer be recognised
     * as inside. Running first is what keeps that test meaningful.
     */
    document.addEventListener('pointerdown', onLeave, true);
    document.addEventListener('focusin', onLeave, true);
    return () => {
      document.removeEventListener('pointerdown', onLeave, true);
      document.removeEventListener('focusin', onLeave, true);
    };
  }, [phase]);

  function onEditorKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    /*
     * A request in flight cannot be recalled, so Escape does not pretend to.
     *
     * While the list is open `MultiCombobox` handles Escape itself and stops it
     * here, so the first press closes the list and the second cancels the edit
     * — which is the order a user expects from two nested layers.
     */
    if (phase !== 'editing') return;
    if (event.key !== 'Escape') return;
    event.preventDefault();
    revert(true);
  }

  const chips = values.map((value) => ({
    value,
    label: options.find((option) => option.value === value)?.label ?? value,
  }));

  const shown = display ?? <RegionChips chips={chips} emptyLabel={emptyLabel} />;

  const hint = 'Leave the field to save. Esc cancels.';
  const line: Notice | null =
    phase === 'saving'
      ? { tone: 'success', text: 'Saving…' }
      : (notice ?? (phase === 'editing' ? { tone: 'success', text: hint } : null));
  /* Three flags, split for the reason `InlineField` splits them: a `partial`
     SAVED, so it must not mark the control invalid nor wear the alert glyph —
     but it carries a refusal and so is still announced. */
  const isProblem =
    phase !== 'saving' && (notice?.tone === 'error' || notice?.tone === 'warning');
  const isSaved = phase === 'idle' && (notice?.tone === 'success' || notice?.tone === 'partial');
  const isAlert = isProblem || (phase !== 'saving' && notice?.tone === 'partial');
  const describedBy = line ? statusId : undefined;

  return (
    <div className="grid min-w-0 gap-1">
      {phase === 'idle' ? (
        <div className="flex min-w-0 items-start gap-1">
          {disabled ? (
            <span className="min-w-0 flex-1">{shown}</span>
          ) : (
            <button
              type="button"
              ref={triggerRef}
              onClick={beginEditing}
              aria-describedby={describedBy}
              className={cx(
                'group -mx-1.5 -my-1 flex min-w-0 flex-1 items-start gap-1.5 rounded-md px-1.5 py-1 text-left',
                'transition-colors duration-150 ease-out motion-reduce:transition-none',
                'hover:bg-surface-hover',
              )}
            >
              {/* The trigger has to name the field: the visible <dt> is not a label. */}
              <span className="sr-only">Edit {label}: </span>
              <span className="min-w-0 flex-1">{shown}</span>
              <PencilGlyph />
            </button>
          )}
        </div>
      ) : (
        <div
          ref={editorRef}
          onKeyDown={onEditorKeyDown}
          aria-busy={phase === 'saving' || undefined}
          className="grid min-w-0 gap-1"
        >
          {/* Inside `editorRef`, so a click on it is not read as a leave, and
              above the picker, because the popover covers whatever sits below
              the trigger. Both are load-bearing — see `editorHeader`. */}
          {hasEditorHeader && <div className="min-w-0">{editorHeader}</div>}
          {/* A <label> cannot name a composite widget, so this is a named group. */}
          <div ref={controlRef} role="group" aria-label={label} className="min-w-0">
            <MultiCombobox
              id={controlId}
              values={draft}
              options={options}
              loading={optionsLoading}
              disabled={phase === 'saving'}
              invalid={isProblem}
              invalidValues={refused}
              aria-describedby={describedBy}
              {...(placeholder === undefined ? {} : { placeholder })}
              {...(searchPlaceholder === undefined ? {} : { searchPlaceholder })}
              {...(footnote === undefined ? {} : { footnote })}
              // Six regions, all active, and a company covering every one of
              // them is a real and ordinary answer.
              selectAll
              onChange={setDraft}
            />
          </div>
        </div>
      )}

      {note && <div className="min-w-0 text-xs text-warning [overflow-wrap:anywhere]">{note}</div>}

      {line && (
        <p
          id={statusId}
          role={isAlert ? 'alert' : 'status'}
          className={cx(
            'm-0 flex min-w-0 items-start gap-1 text-xs [overflow-wrap:anywhere]',
            phase === 'saving' || notice === null ? 'text-fg-subtle' : noticeToneClass(notice.tone),
          )}
        >
          {/* Never colour alone: the glyph and the wording each carry it too. */}
          {isProblem && <AlertGlyph />}
          {isSaved && <CheckGlyph />}
          <span className="min-w-0">{line.text}</span>
        </p>
      )}
    </div>
  );
}

/**
 * Every member, always. A company can hold four regions and all four are the
 * answer — a "+2 more" here would hide half of a four-item fact to save one
 * line of space.
 */
function RegionChips({
  chips,
  emptyLabel,
}: {
  chips: { value: string; label: string }[];
  emptyLabel: string;
}) {
  if (chips.length === 0) return <span className="text-fg-subtle">{emptyLabel}</span>;
  return (
    <span className="flex flex-wrap items-center gap-1">
      {chips.map((chip) => (
        <Badge key={chip.value} tone="neutral" variant="outline">
          {chip.label}
        </Badge>
      ))}
    </span>
  );
}

function glyphProps(className: string) {
  return {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    className,
  };
}

/**
 * The edit affordance, drawn the same way `InlineField` draws it — faintly
 * visible at rest at every width, brightening on hover or focus. It used to be
 * `opacity-0` above `sm`, which `InlineField` records as having been reported
 * as a missing feature; matched to `InlineField` in the 2026-10-11 UI sweep.
 */
function PencilGlyph() {
  return (
    <svg
      {...glyphProps(
        cx(
          'mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-subtle opacity-60',
          'transition-opacity duration-150 ease-out motion-reduce:transition-none',
          'group-hover:opacity-100 group-focus-visible:opacity-100',
        ),
      )}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function AlertGlyph() {
  return (
    <svg {...glyphProps('mt-px h-3.5 w-3.5 shrink-0')}>
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg {...glyphProps('mt-px h-3.5 w-3.5 shrink-0')}>
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}
